import { API_BASE_URL } from '../constant/api-url';
import { SESSION_HEADER } from '../telemetry/http';
import { getSessionId } from '../telemetry';
import {
  AiFileInfo,
  AiScheduleInfo,
  ChatErrorKind,
  DebugItem,
} from '../types/IAiConversation';

// A turn that goes quiet for this long is abandoned. The API sends a keepalive comment every
// 15 s, so a healthy stream never gets near it.
export const STALE_TIMEOUT_MS = 60_000;

// Normalised events, flattened from the API's `event: <name>` + `data: <payload>` frames.
export type AiStreamEvent =
  | { type: 'conversation'; conversationId: number }
  | { type: 'status'; message: string }
  | { type: 'text'; text: string }
  | { type: 'textReset' }
  | { type: 'debug'; debug: DebugItem }
  | { type: 'refresh'; files: AiFileInfo[]; schedules: AiScheduleInfo[] }
  | { type: 'done'; messageId?: number }
  | { type: 'error'; message: string; kind: ChatErrorKind; status?: number };

export interface StreamChatParams {
  token: string;
  message: string;
  // Omit on the first turn; the API reloads history from this id after.
  conversationId: number | null;
  signal?: AbortSignal;
  onEvent: (event: AiStreamEvent) => void;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

// Turns one frame into an event. The API sends the payload alone (no envelope), but an
// `{ eventType, data }` envelope is tolerated too. Unknown events are dropped.
export function toEvent(name: string, raw: string): AiStreamEvent | null {
  let parsed: Record<string, unknown> = {};
  if (raw.trim().length) {
    try {
      parsed = asRecord(JSON.parse(raw));
    } catch {
      parsed = { content: raw };
    }
  }

  const envelopeType = asString(parsed.eventType ?? parsed.type, '');
  const payload =
    envelopeType && typeof parsed.data === 'object' && parsed.data !== null
      ? asRecord(parsed.data)
      : parsed;

  const type = (name || envelopeType).toLowerCase();

  switch (type) {
    case 'conversation': {
      const info = asRecord(payload.conversation);
      const id = info.conversationId ?? payload.conversationId;
      return id != null
        ? { type: 'conversation', conversationId: Number(id) }
        : null;
    }
    case 'status':
      return { type: 'status', message: asString(payload.content) };
    case 'text':
      return { type: 'text', text: asString(payload.content) };
    case 'textreset':
      return { type: 'textReset' };
    case 'debug': {
      const info = asRecord(payload.debug ?? payload);
      const kind = asString(info.debugEventType);
      if (kind !== 'tool_call' && kind !== 'tool_result') return null;
      return {
        type: 'debug',
        debug: {
          kind,
          tool: asString(info.tool),
          input: typeof info.input === 'string' ? info.input : undefined,
          result: typeof info.result === 'string' ? info.result : undefined,
        },
      };
    }
    case 'refresh': {
      const info = asRecord(payload.refresh ?? payload);
      const files = Array.isArray(info.files)
        ? info.files
            .map((f) => asRecord(f))
            .filter((f) => typeof f.id === 'number')
            .map((f) => ({
              id: f.id as number,
              fileName: asString(f.fileName, 'file'),
              sizeBytes: typeof f.sizeBytes === 'number' ? f.sizeBytes : 0,
              downloadUrl: asString(f.downloadUrl),
            }))
        : [];
      const schedules = Array.isArray(info.schedules)
        ? info.schedules
            .map((x) => asRecord(x))
            .filter((x) => typeof x.id === 'number')
            .map((x) => ({
              id: x.id as number,
              name: asString(x.name, 'Schedule'),
              runs: asString(x.runs),
              nextRunUtc:
                typeof x.nextRunUtc === 'string' ? x.nextRunUtc : null,
            }))
        : [];
      return { type: 'refresh', files, schedules };
    }
    case 'done':
      return {
        type: 'done',
        messageId:
          typeof payload.messageId === 'number' ? payload.messageId : undefined,
      };
    case 'error':
      return {
        type: 'error',
        message: asString(
          payload.content ?? payload.message,
          'Something went wrong',
        ),
        kind: 'server',
      };
    default:
      return null;
  }
}

// Reads a non-2xx body for the API's `{ message }` (503 not configured, 400, ...), or a short
// plain-text reason; HTML error pages are skipped.
const readFailureMessage = async (response: Response): Promise<string> => {
  const text = (await response.text().catch(() => '')).trim();
  if (text) {
    try {
      const parsed = asRecord(JSON.parse(text));
      const message = asString(parsed.message ?? parsed.Message);
      if (message) return message;
    } catch {
      // not JSON
    }
    if (!text.startsWith('<') && text.length <= 200) return text;
  }
  return `Request failed (${response.status})`;
};

// Streams one chat turn over raw fetch SSE: EventSource cannot POST or send a Bearer token.
// The body carries no history; the API reloads it from the conversation id.
export async function streamChat({
  token,
  message,
  conversationId,
  signal,
  onEvent,
}: StreamChatParams): Promise<void> {
  const url =
    conversationId != null
      ? `${API_BASE_URL}api/ai/conversations/${conversationId}/chat`
      : `${API_BASE_URL}api/ai/conversations/chat`;

  // Local controller so the stream self-aborts after a silence, while still honouring the
  // caller's abort (Stop button, a superseding turn).
  const controller = new AbortController();
  const abortFromParent = () => controller.abort();
  signal?.addEventListener('abort', abortFromParent);
  if (signal?.aborted) controller.abort();

  let staleTimer: ReturnType<typeof setTimeout> | undefined;
  let staleTimedOut = false;
  const resetStaleTimer = () => {
    if (staleTimer) clearTimeout(staleTimer);
    staleTimer = setTimeout(() => {
      staleTimedOut = true;
      controller.abort();
    }, STALE_TIMEOUT_MS);
  };

  let eventName: string | null = null;
  let dataLines: string[] = [];
  let terminal = false;

  const dispatch = () => {
    if (eventName === null && dataLines.length === 0) return;
    const name = eventName ?? '';
    const raw = dataLines.join('\n');
    eventName = null;
    dataLines = [];
    const event = toEvent(name, raw);
    if (event) {
      onEvent(event);
      terminal = event.type === 'done' || event.type === 'error';
    }
  };

  let buffer = '';
  const handleLine = (rawLine: string) => {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (line === '') {
      dispatch(); // a blank line terminates an SSE frame
    } else if (line.startsWith(':')) {
      // keepalive comment
    } else if (line.startsWith('event:')) {
      // a new event line before a blank line terminates the previous frame
      if (eventName !== null || dataLines.length) dispatch();
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      let value = line.slice(5);
      if (value.startsWith(' ')) value = value.slice(1);
      dataLines.push(value);
    } else if (line.startsWith('{')) {
      // Tolerate newline-delimited JSON (a bare `{...}` frame per line).
      if (eventName !== null || dataLines.length) dispatch();
      dataLines.push(line);
      dispatch();
    }
    // any other field: ignored
  };
  const flushLines = () => {
    let newlineIndex = buffer.indexOf('\n');
    while (!terminal && newlineIndex !== -1) {
      const line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);
      handleLine(line);
      newlineIndex = buffer.indexOf('\n');
    }
  };

  try {
    // Armed before the request: a fetch that never returns headers must time out too.
    resetStaleTimer();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
        [SESSION_HEADER]: getSessionId(),
      },
      body: JSON.stringify({ message }),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      onEvent({
        type: 'error',
        message: await readFailureMessage(response),
        kind: 'http',
        status: response.status,
      });
      return;
    }

    resetStaleTimer();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let finished = false;
    while (!finished) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) {
        finished = true;
      } else {
        resetStaleTimer();
        buffer += decoder.decode(value, { stream: true });
        flushLines();
        if (terminal) {
          // eslint-disable-next-line no-await-in-loop
          await reader.cancel();
          finished = true;
        }
      }
    }

    // Flush a trailing frame that ended without a blank line.
    if (!terminal) {
      buffer += decoder.decode();
      if (buffer.length && !buffer.endsWith('\n')) buffer += '\n';
      flushLines();
      dispatch();
    }

    // Ended without a done/error frame (dropped connection, non-SSE body): surface it.
    if (!terminal) {
      onEvent({
        type: 'error',
        message: 'The response ended unexpectedly. Please try again.',
        kind: 'network',
      });
    }
  } catch (e) {
    if (signal?.aborted) return;
    if (staleTimedOut) {
      onEvent({
        type: 'error',
        message: 'The response timed out. Please try again.',
        kind: 'timeout',
      });
      return;
    }
    if (controller.signal.aborted) return;
    onEvent({
      type: 'error',
      message: e instanceof Error ? e.message : 'Streaming failed',
      kind: 'network',
    });
  } finally {
    if (staleTimer) clearTimeout(staleTimer);
    signal?.removeEventListener('abort', abortFromParent);
  }
}
